import { useEffect, useRef, useState } from 'react';

import { PROJECT_VARIANTS } from '../../constants';
import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import { fetchCache, mediaCache, setInCache } from '../../service/mediaCache';
import { useSelector } from 'react-redux';

const useActiv8Media = ({ id, mediaType, projectVariant = PROJECT_VARIANTS.ADMIN, onLoadMedia }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const controllerRef = useRef(null);

  const { page, sort, size, searchQuery, filters } = useSelector((state) => state.activ8.tableSettings);

  const handleError = () => {
    setMediaUrl(null);
    setHasError(true);
    setIsLoading(false);
  };

  const handleSuccess = (response) => {
    const blobUrl = URL.createObjectURL(new Blob([response.data]));
    setInCache(mediaCache, id, blobUrl);
    setMediaUrl(blobUrl);
    onLoadMedia?.(blobUrl);

    setHasError(false);
  };

  const handleLoadMedia = async () => {
    setIsLoading(true);

    const cachedPromise = fetchCache.get(id);

    const fetchPromise =
      cachedPromise ||
      sendRequest(
        () => {
          const controller = new AbortController();
          controllerRef.current = controller;

          if (projectVariant === PROJECT_VARIANTS.ADMIN) {
            return callApi.get(`/api/activ8/media/display?contentId=${id}`, {
              responseType: 'arraybuffer',
              signal: controller.signal,
            });
          }
        },
        {
          onError: (e) => {
            if (e.message.includes('canceled')) {
              fetchCache.delete(id);
              return;
            }

            fetchCache.delete(id);
            handleError();
          },
        },
      );
    setInCache(fetchCache, id, fetchPromise);

    const response = await fetchPromise;
    handleSuccess(response);

    setIsLoading(false);
  };

  useEffect(() => {
    if (!id) return;

    if (mediaCache.has(id)) {
      setMediaUrl(mediaCache.get(id));
      onLoadMedia?.(mediaCache.get(id));
      return;
    }

    handleLoadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mediaType, projectVariant]);

  useEffect(() => {
    return () => {
      if (mediaUrl && !Array.from(mediaCache.values()).includes(mediaUrl)) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrl]);

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [page, sort, size, searchQuery, filters]);

  return {
    mediaUrl,
    error: hasError,
    isLoading,
    setError: setHasError,
  };
};

export default useActiv8Media;
